/* Same-origin API client.
 *
 * REPLACES the old fetch monkeypatch (service/review/index.html ~799-804):
 * the old code rebound window.fetch so a relative URL string always
 * absolutized against `location.origin` (not `location.href`, which could
 * carry `user:pass@host` basic-auth credentials that make the Fetch spec
 * reject the resulting Request). `new URL(apiBase + path, location.origin)`
 * gets the same credential-free base for free on every call site, so the
 * monkeypatch itself is intentionally NOT ported here (F2a brief step 2).
 *
 * apiBase is always '' — same-origin. In dev, vite.config.ts's proxy maps
 * every backend-owned path (see `proxiedPaths`) to the FastAPI service; in
 * prod, service/main.py serves the built hub itself, so same-origin is
 * correct there too.
 */
export const apiBase = '';

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/** Builds a fully-qualified same-origin URL for `apiBase + path` — the same
 * base resolution `request()` uses, exposed for non-JSON/static fetches
 * (result JSON files served as text, the run-picker's file list entries). */
export function rawUrl(path: string): string {
  return new URL(apiBase + path, location.origin).toString();
}

/** JSON request/response helper. Error-body parsing mirrors the old WS.api
 * verbatim (service/review/index.html ~3690-3699): on a non-ok response,
 * try to read `{detail}` from the JSON body; the thrown error's message
 * prefers `detail.error`, then `detail` itself, then `HTTP <status>` — and
 * `status`/`detail` are attached to the error (ApiError here; the old code
 * used a plain Error with those two properties bolted on).
 *
 * `credentials: 'same-origin'` (Phase 0 auth, Task 5): sends the fb_session
 * cookie (service/auth.py's SESSION_COOKIE, httponly/samesite=lax) on every
 * same-origin call — required for every route this task's brief lists as
 * newly gated (`/clients`, `/tasks`, `/notes`, `/research`, `/impact`, …).
 * A 401 on anything OTHER than the /auth/* endpoints themselves means the
 * session died mid-use (expiry, server restart) — re-show the sign-in gate
 * reactively rather than leaving the caller to fail silently. Dynamic
 * import (not a static one) because lib/auth.ts imports `request`/`ApiError`
 * from THIS module — a static import the other way would be circular. */
export async function request<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const url = rawUrl(path);
  const headers = Object.assign({ 'Content-Type': 'application/json' }, init?.headers || {});
  const res = await fetch(url, Object.assign({}, init, { headers, credentials: 'same-origin' }));
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch (e) { /* body wasn't JSON / already consumed */ }
    const message = (detail && typeof detail === 'object' && (detail as { error?: unknown }).error) || detail || ('HTTP ' + res.status);
    if (res.status === 401 && !path.startsWith('/auth/')) {
      // A 401 here (not on /auth/* — init()'s own /auth/me handles that) is a
      // session that died mid-use. `reauth: true` makes the gate show
      // expiry-specific copy and re-auth NON-destructively (no reload, drafts
      // preserved — design review #1). .catch() swallows a showGate() failure
      // (e.g. no DOM — the vitest unit suite runs this module under Node with
      // no `document`); the caller below still gets the real ApiError either way.
      import('../lib/auth.ts').then((m) => m.AUTH.showGate({ reauth: true })).catch(() => {});
    }
    throw new ApiError(String(message), res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
